import type { FC } from 'react';
import { PapiDeviation, PapiGallection } from '@wix/da-papi-types';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import StudioHeaderMobile from '../../../StudioHeaderMobile';
import { useDraftsSelect } from '../../../../hooks/useDraftsSelect';
import {
  STUDIO_DEVIATIONS_STREAM,
  GALLERY_SUBFOLDERS_STREAM,
} from '../../../../../constants';
import { StudioSection } from '../../../../types/store';
import PublishedTabs from '../../../published/PublishedTabs';
import DeviationsTab from '../../_partials/DeviationsTab';
import LayoutContainer from '../../_partials/LayoutContainer';
import StudioBlankSlate from '../../../StudioBlankSlate';
import PostsBlankSlate from '../../../StudioBlankSlate/sections/PostsBlankSlate';
import FolderListMobile from '../../../published/FolderList/FolderListMobile';
import TierListMobile from '../../../published/TierList/TierListMobile';
import FreeBoostsPromoBanner from '../../../FreeBoostsPromoBanner';

import s from './PublishedLayout.scss';

export interface Props {
  activeSection: StudioSection;
  folder?: PapiGallection;
  isSubfoldersPage?: boolean;
  publishedFolderId?: number;
  publishedTierId?: number;
  tiers?: PapiDeviation[];
  contentType?: string;
}

const PublishedLayout: FC<Props> = ({
  folder,
  activeSection,
  isSubfoldersPage,
  publishedFolderId,
  publishedTierId,
  tiers,
  contentType,
}) => {
  const { streamId, streamConfig, streamThrottle } = STUDIO_DEVIATIONS_STREAM;
  const { items: deviations, isFetching } = useNormalizedDeviationStream(
    streamId,
    streamThrottle,
    { ...streamConfig }
  );

  const {
    selected,
    selectedCount,
    allIsSelected,
    hasSelection,
    onSelect,
    onSelectAll,
  } = useDraftsSelect(deviations);

  const hasDeviations = deviations.length > 0;
  const isPostsContent = contentType === 'posts';
  const isMyGalleriesPage = activeSection === StudioSection.PUBLISHED_GALLERIES;
  const isMyTiersPage = activeSection === StudioSection.PUBLISHED_TIERS;
  return (
    <LayoutContainer>
      <StudioHeaderMobile
        selectedCount={selectedCount}
        allIsSelected={allIsSelected}
        onSelectAll={onSelectAll}
      />
      {(!hasDeviations ||
        isMyGalleriesPage ||
        isMyTiersPage ||
        isSubfoldersPage) && <PublishedTabs />}
      {!hasDeviations && !isMyGalleriesPage && (
        <>
          {isPostsContent && <PostsBlankSlate />}
          {!isPostsContent && !isSubfoldersPage && <StudioBlankSlate />}
        </>
      )}
      {hasDeviations &&
        !isMyGalleriesPage &&
        !isMyTiersPage &&
        !isSubfoldersPage && (
          <DeviationsTab
            elementBeforeList={
              !hasSelection && (
                <>
                  <PublishedTabs key="tabs" />
                  <FreeBoostsPromoBanner />
                </>
              )
            }
            deviations={deviations}
            isFetching={isFetching}
            selected={selected}
            hasSelection={hasSelection}
            onSelect={onSelect}
          />
        )}
      {(isMyGalleriesPage || isSubfoldersPage) && (
        <div className={s['folders-wrapper']}>
          <FolderListMobile
            streamId={GALLERY_SUBFOLDERS_STREAM.streamId}
            withCreateButton
            withFolderActions
          />
        </div>
      )}
      {isMyTiersPage && (
        <>
          {tiers && tiers.length > 0 ? (
            <div className={s['folders-wrapper']}>
              <TierListMobile tiers={tiers} />
            </div>
          ) : (
            <StudioBlankSlate />
          )}
        </>
      )}
    </LayoutContainer>
  );
};
PublishedLayout.displayName = 'PublishedLayout';

export default PublishedLayout;
